import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';

// Public/member API switch. Module-level on purpose: a scoped `const P` breaks
// the moment a nested component or modal makes its own api.get call.
function PUB() {
  return (typeof window !== "undefined" &&
    window.location.pathname.startsWith("/club")) ? "/public" : "";
}

// ── Theme (matches StatsV2Pages) ────────────────────────────────
const NAVY = '#1E3A6E', GOLD = '#F5C518', RED = '#dc2626', BLUE = '#2563eb', GRN = '#16a34a', SLATE = '#64748b';
const num = (n) => (n == null ? '0' : Number(n).toLocaleString('en-IN'));
const f1  = (n) => (n == null ? '0' : Number(n).toFixed(n % 1 === 0 ? 0 : 1));

// ════════════════════════════════════════════════════════════════
// WAGON WHEEL  — built from matchups.direction_split (real per-zone
// runs / fours / sixes). RHB: OFF left, LEG right, batsman faces down.
// ════════════════════════════════════════════════════════════════
const ZONE_ANGLE = {
  // angle from straight-down (0). + = off (left), - = leg (right). RHB.
  'Straight': 0, 'Long-Off': 22, 'Mid-Off': 40, 'Cover': 60, 'Point': 90, 'Third Man': 148,
  'Long-On': -22, 'Mid-On': -40, 'Cow Corner': -55, 'Mid-Wicket': -72, 'Square Leg': -92, 'Fine Leg': -148,
};

function WagonWheel({ zones, selected, onSelect, lhb }) {
  const CX = 170, CY = 175, RX = 150, RY = 140, PH = 30;
  const OX = CX, OY = CY - PH;            // striker at top of central pitch
  const edge = (aDeg) => {
    const a = aDeg * Math.PI / 180, dx = -Math.sin(a), dy = Math.cos(a);
    const ox = OX - CX, oy = OY - CY;
    const A = (dx * dx) / (RX * RX) + (dy * dy) / (RY * RY);
    const B = 2 * ((ox * dx) / (RX * RX) + (oy * dy) / (RY * RY));
    const C = (ox * ox) / (RX * RX) + (oy * oy) / (RY * RY) - 1;
    const t = (-B + Math.sqrt(Math.max(B * B - 4 * A * C, 0))) / (2 * A);
    return { R: t, dx, dy };
  };
  const fan = (a, n, spread) => Array.from({ length: n }, (_, i) =>
    n === 1 ? a : a + spread * (i / (n - 1) - 0.5));
  // For a left-hand batter the field is mirrored: off side moves to the right,
  // leg side to the left. Zone names stay the same; we flip the angle sign.
  const zoneAngle = (zone) => { const v = ZONE_ANGLE[zone]; return v === undefined ? undefined : (lhb ? -v : v); };

  // Only real boundaries are drawn as lines (4s blue, 6s red). 1-3s are not
  // drawn (we don't store single-by-single directions) — they're summed into
  // the zone label total instead. This keeps every line truthful.
  const lines = [];
  let key = 0;
  (zones || []).forEach((z) => {
    const a = zoneAngle(z.zone);
    if (a === undefined) return;
    const fours = +z.fours || 0, sixes = +z.sixes || 0;
    if ((+z.runs || 0) <= 0) return;
    const op = selected && selected !== z.zone ? 0.08 : 0.9;
    fan(a, sixes, 20).forEach((ang) => {
      const { R, dx, dy } = edge(ang);
      const ex = OX + dx * R * 1.03, ey = OY + dy * R * 1.03;
      const mx = (OX + ex) / 2, my = (OY + ey) / 2, px = -dy, py = dx, bow = R * 0.10 * (key % 2 ? 1 : -1);
      lines.push(<path key={`s${key++}`} d={`M${OX} ${OY} Q${(mx + px * bow).toFixed(1)} ${(my + py * bow).toFixed(1)} ${ex.toFixed(1)} ${ey.toFixed(1)}`}
        fill="none" stroke={RED} strokeWidth="1.5" strokeLinecap="round" opacity={op} />);
    });
    fan(a, fours, 20).forEach((ang) => {
      const { R, dx, dy } = edge(ang);
      const ex = OX + dx * R * 0.98, ey = OY + dy * R * 0.98;
      lines.push(<line key={`f${key++}`} x1={OX} y1={OY} x2={ex.toFixed(1)} y2={ey.toFixed(1)} stroke={BLUE} strokeWidth="1.4" strokeLinecap="round" opacity={op} />);
    });
  });

  // zone run-total labels — smaller, tappable, highlight when selected
  const labels = (zones || []).filter(z => zoneAngle(z.zone) !== undefined && (+z.runs || 0) > 0).map((z, i) => {
    const { R, dx, dy } = edge(zoneAngle(z.zone));
    const lx = OX + dx * (R + 14), ly = OY + dy * (R + 14);
    const txt = `${z.zone} · ${z.runs}`, w = txt.length * 4.3 + 8;
    const on = selected === z.zone;
    return (
      <g key={`l${i}`} onClick={() => onSelect && onSelect(on ? null : z.zone)} style={{ cursor: 'pointer' }}>
        <rect x={(lx - w / 2).toFixed(1)} y={(ly - 7).toFixed(1)} width={w.toFixed(1)} height="13" rx="6.5" fill={on ? NAVY : '#fff'} stroke={on ? NAVY : '#e3e8f0'} />
        <text x={lx.toFixed(1)} y={(ly + 2).toFixed(1)} textAnchor="middle" fontSize="7" fill={on ? '#fff' : '#3f4f6b'} fontFamily="Rajdhani,sans-serif" fontWeight="700">{txt}</text>
      </g>
    );
  });

  const stripes = [];
  for (let y = CY - RY, i = 0; y < CY + RY; y += 13, i++) {
    stripes.push(<rect key={`st${i}`} x={CX - RX} y={y} width={2 * RX} height="13" fill={i % 2 === 0 ? '#54C97D' : '#46BE70'} />);
  }

  return (
    <svg viewBox="-52 -6 444 412" width="100%" preserveAspectRatio="xMidYMid meet" aria-label="Wagon wheel">
      <defs><clipPath id="wwg"><ellipse cx={CX} cy={CY} rx={RX} ry={RY} /></clipPath></defs>
      <g clipPath="url(#wwg)">{stripes}</g>
      <ellipse cx={CX} cy={CY} rx={RX} ry={RY} fill="none" stroke="#fff" strokeWidth="2.5" />
      <ellipse cx={CX} cy={CY} rx="64" ry="60" fill="none" stroke="rgba(255,255,255,.75)" strokeWidth="1" strokeDasharray="4 5" />
      <text x={CX - RX + 12} y={CY + 3} fontFamily="Rajdhani,sans-serif" fontWeight="700" fontSize="10" letterSpacing="2" fill="rgba(255,255,255,.95)">{lhb ? 'LEG' : 'OFF'}</text>
      <text x={CX + RX - 12} y={CY + 3} textAnchor="end" fontFamily="Rajdhani,sans-serif" fontWeight="700" fontSize="10" letterSpacing="2" fill="rgba(255,255,255,.95)">{lhb ? 'OFF' : 'LEG'}</text>
      <rect x={CX - 9} y={CY - PH} width="18" height={2 * PH} rx="3" fill="#e8dcbf" />
      {lines}
      <g stroke="#2c2410" strokeWidth="1.2">
        <line x1={CX - 3} y1={CY + PH - 4} x2={CX - 3} y2={CY + PH + 4} /><line x1={CX} y1={CY + PH - 4} x2={CX} y2={CY + PH + 4} /><line x1={CX + 3} y1={CY + PH - 4} x2={CX + 3} y2={CY + PH + 4} />
      </g>
      <g><circle cx={OX - 8} cy={OY + 5} r="3" fill="#15294d" stroke="#fff" strokeWidth="0.7" /><line x1={OX - 8} y1={OY + 8} x2={OX - 8} y2={OY + 16} stroke="#15294d" strokeWidth="1.8" /><line x1={OX - 8} y1={OY + 10} x2={OX - 2} y2={OY + 19} stroke="#7a4a1f" strokeWidth="2.3" strokeLinecap="round" /></g>
      {labels}
    </svg>
  );
}

// ── Run-trend bar chart from runs_trend [{runs}] ────────────────
function RunTrend({ trend }) {
  const rows = (trend || []).slice(-24);
  if (!rows.length) return <div style={{ padding: 16, color: '#94a3b8', fontSize: '0.8rem' }}>No innings data.</div>;
  const max = Math.max(100, ...rows.map(r => +r.runs || 0));
  const step = (470 - 34) / rows.length;
  return (
    <svg viewBox="0 0 470 140" width="100%" preserveAspectRatio="xMidYMid meet">
      <line x1="28" y1="18" x2="28" y2="120" stroke="#dde3ec" /><line x1="28" y1="120" x2="462" y2="120" stroke="#dde3ec" />
      {[0, 50, 100].map(g => {
        const gy = 120 - (g / max) * 100;
        return <g key={g}><line x1="28" y1={gy} x2="462" y2={gy} stroke="#eef1f6" /><text x="23" y={gy + 3} textAnchor="end" fontSize="9" fill="#9aa7bd" fontFamily="Rajdhani,sans-serif">{g}</text></g>;
      })}
      {rows.map((r, i) => {
        const v = +r.runs || 0, h = (v / max) * 100, x = 34 + i * step;
        return <rect key={i} x={x.toFixed(0)} y={(120 - h).toFixed(0)} width={(step * 0.62).toFixed(0)} height={h.toFixed(0)} rx="2" fill={v >= 50 ? GOLD : NAVY} />;
      })}
    </svg>
  );
}

// ── Dismissals donut from dismissal_breakdown [{dismissal,count}] ─
const DIS_COLOR = { caught: BLUE, bowled: NAVY, lbw: GOLD, run_out: RED, stumped: GRN, 'hit_wicket': '#7c3aed' };
function DismissalDonut({ rows }) {
  const segs = (rows || []).filter(r => (+r.count || 0) > 0)
    .map(r => ({ label: String(r.dismissal).replace(/_/g, ' '), value: +r.count, color: DIS_COLOR[r.dismissal] || SLATE }));
  const tot = segs.reduce((s, x) => s + x.value, 0);
  if (!tot) return <div style={{ padding: 16, color: '#94a3b8', fontSize: '0.8rem' }}>No dismissals.</div>;
  const circ = 2 * Math.PI * 46; let off = 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
      <svg viewBox="0 0 180 180" width="148" height="148">
        {segs.map((s, i) => {
          const dash = circ * s.value / tot;
          const el = <circle key={i} cx="90" cy="90" r="46" fill="none" stroke={s.color} strokeWidth="20" strokeDasharray={`${dash.toFixed(2)} ${(circ - dash).toFixed(2)}`} strokeDashoffset={(-off).toFixed(2)} transform="rotate(-90 90 90)" />;
          off += dash; return el;
        })}
        <text x="90" y="86" textAnchor="middle" fontSize="22" fontFamily="Rajdhani,sans-serif" fontWeight="700" fill={NAVY}>{tot}</text>
        <text x="90" y="102" textAnchor="middle" fontSize="9" letterSpacing="1" fill="#9aa7bd" fontFamily="Rajdhani,sans-serif">DISMISSALS</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {segs.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: '#4a5a76', textTransform: 'capitalize' }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color }} />{s.label} · {s.value}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── small UI atoms ──────────────────────────────────────────────
const Card = ({ title, hint, children, style }) => (
  <div style={{ background: '#fff', border: '1px solid rgba(30,58,110,.10)', borderRadius: 14, padding: '14px 16px', margin: '0 14px 12px', ...style }}>
    {title && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, letterSpacing: '.07em', fontSize: '0.88rem', color: NAVY }}>{title}</div>
      {hint && <div style={{ fontSize: '0.62rem', letterSpacing: '.07em', color: '#9aa7bd' }}>{hint}</div>}
    </div>}
    {children}
  </div>
);
const Kpi = ({ n, l, s, gold }) => (
  <div style={{ background: '#fff', border: '1px solid rgba(30,58,110,.10)', borderRadius: 12, padding: '12px 8px', textAlign: 'center', borderBottom: gold ? `3px solid ${GOLD}` : undefined }}>
    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1.35rem', lineHeight: 1, color: NAVY }}>{n}</div>
    <div style={{ fontSize: '0.6rem', letterSpacing: '.06em', color: '#8190a8', marginTop: 5 }}>{l}</div>
    {s && <div style={{ fontSize: '0.56rem', color: '#9aa7bd', marginTop: 2 }}>{s}</div>}
  </div>
);
// "30th over · 4 Oct 2026" — over in cricket numbering, match date as stored.
const ordinal = (n) => { const v = n % 100; return n + (['th','st','nd','rd'][(v - 20) % 10] || ['th','st','nd','rd'][v] || 'th'); };
const overWhen = (o) => {
  if (!o) return '';
  const ov = o.over_no != null ? Number(o.over_no) : Number(o.over_number) + 1;
  const d = o.match_day ? new Date(`${o.match_day}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  return [`${ordinal(ov)} over`, d].filter(Boolean).join(' · ');
};
const Matchup = ({ accent, label, who, meta }) => (
  <div style={{ background: '#f7f9fc', border: '1px solid rgba(30,58,110,.10)', borderLeft: `3px solid ${accent}`, borderRadius: 10, padding: '10px 11px' }}>
    <div style={{ fontSize: '0.6rem', letterSpacing: '.05em', color: '#8190a8', fontFamily: 'Rajdhani,sans-serif', fontWeight: 600 }}>{label}</div>
    <div style={{ fontWeight: 700, fontSize: '0.85rem', marginTop: 2, color: NAVY }}>{who}</div>
    {meta && <div style={{ fontSize: '0.66rem', color: '#5f6f8a', marginTop: 1 }}>{meta}</div>}
  </div>
);
const Bar = ({ lab, pct, val, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: '0.78rem', padding: '4px 0' }}>
    <div style={{ width: 90, color: '#4a5a76' }}>{lab}</div>
    <div style={{ flex: 1, height: 9, borderRadius: 5, background: '#eef1f6', overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${pct}%`, borderRadius: 5, background: color || NAVY }} />
    </div>
    <div style={{ width: 40, textAlign: 'right', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, color: NAVY }}>{val}</div>
  </div>
);

// ════════════════════════════════════════════════════════════════
export default function PlayerStatsRedesign() {
  const { memberId } = useParams();
  const [year, setYear] = useState('all');
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [zoneSel, setZoneSel] = useState(null);
  const [tab, setTab] = useState('BATTING');

  useEffect(() => {
    let alive = true; setLoading(true); setErr(null);
    Promise.all([
      api.get(`${PUB()}/stats-v2/player/${memberId}?year=${year}`),
      api.get(`${PUB()}/stats-v2/players?year=all&includeInactive=true`),
    ]).then(([p, list]) => {
      if (!alive) return;
      if (p.data?.error) { setErr(p.data.error); setLoading(false); return; }
      setData(p.data);
      setMeta((list.data?.players || []).find(x => String(x.id) === String(memberId)) || null);
      setLoading(false);
    }).catch(e => { if (alive) { setErr(e.message || 'load failed'); setLoading(false); } });
    return () => { alive = false; };
  }, [memberId, year]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: SLATE }}>Loading…</div>;
  if (err) return <div style={{ padding: 40, textAlign: 'center', color: RED }}>Couldn't load: {err}</div>;

  const b = data.batting || {}, mi = data.matchups || {}, bi = mi.bowler_insight || {}, di = mi.direction_insight || {};
  // Nemesis from the dismissal list (excludes run-outs, no min-balls gate). Show ALL tied.
  const nemList = (mi.nemesis || []).map(n => ({ name: n.display_name, c: +n.times_out || 0 })).filter(n => n.c > 0);
  const nemTop = nemList.length ? Math.max(...nemList.map(n => n.c)) : 0;
  const nemTied = nemList.filter(n => n.c === nemTop).map(n => n.name);
  const joinNames = (arr) => arr.length <= 1 ? (arr[0] || '—') : arr.length === 2 ? `${arr[0]} & ${arr[1]}` : `${arr.slice(0, -1).join(', ')} & ${arr[arr.length - 1]}`;
  const zoneRow = (mi.direction_split || []).find(z => z.zone === zoneSel);
  const isLHB = /lhb|left/i.test(meta?.batting_hand || '');
  // ── data for the other tabs ──
  const bw = data.bowling || {}, bwi = mi.batter_insight || {}, fd = data.fielding || {}, pts = data.points || {}, cap = data.captaincy || {};
  const bestFig = bw.best_figures ? `${bw.best_figures.wickets}/${bw.best_figures.runs}` : '—';
  const trend = b.runs_trend || [];                       // chronological, oldest→newest
  const lastN = (n) => trend.slice(-n);
  const outsIn = (rows) => rows.filter(r => r.dismissal && r.dismissal !== 'not_out').length;
  const sumRuns = (rows) => rows.reduce((s, r) => s + (+r.runs || 0), 0);
  const sumBalls = (rows) => rows.reduce((s, r) => s + (+r.balls || 0), 0);
  const l10 = lastN(10);
  const formAvg = outsIn(l10) ? sumRuns(l10) / outsIn(l10) : sumRuns(l10);
  const formSR = sumBalls(l10) ? sumRuns(l10) / sumBalls(l10) * 100 : 0;
  const results = data.recent_form || [];                 // [{date, result: 'MOM'|'W'|'L'}]
  const phaseEcoMax = Math.max(1, +bw.phase?.powerplay_economy || 0, +bw.phase?.middle_economy || 0, +bw.phase?.death_economy || 0);
  const bowlWktMax = Math.max(...(mi.vs_batters || []).map(x => +x.wickets || 0), 1);
  // Bunny (most dismissed) from the same list shown below — tie-aware so headline matches.
  const vbList = (mi.vs_batters || []).map(x => ({ name: x.display_name, c: +x.wickets || 0 })).filter(x => x.c > 0);
  const vbTop = vbList.length ? Math.max(...vbList.map(x => x.c)) : 0;
  const vbTied = vbList.filter(x => x.c === vbTop).map(x => x.name);
  const seasons = data.seasons || [];
  const fiftyPlus = (+b.fifties || 0) + (+b.centuries || 0);
  const distMax = Math.max(1, +b.scores_1_9 || 0, +b.scores_10_19 || 0, +b.scores_20_29 || 0, +b.scores_30_49 || 0, fiftyPlus, +b.ducks || 0);
  const name = meta?.display_name || 'Player';
  const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div style={{ paddingBottom: 80, background: '#EEF1F5', minHeight: '100vh' }}>
      {/* hero */}
      <div style={{ background: NAVY, color: '#fff', padding: '16px 16px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', border: `2px solid ${GOLD}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1.05rem', color: GOLD, overflow: 'hidden' }}>
            {meta?.photo_url ? <img src={meta.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1.25rem', color: GOLD }}>{name}</div>
            <div style={{ fontSize: '0.72rem', color: '#bcccea' }}>{meta?.ucc_id}{meta?.jersey_no ? ` · #${meta.jersey_no}` : ''}</div>
          </div>
        </div>
        {/* year chips */}
        <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
          {['all', ...seasons].map(y => (
            <button key={y} onClick={() => setYear(String(y))}
              style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 600, fontSize: '0.72rem', padding: '4px 11px', borderRadius: 14, border: 'none', cursor: 'pointer',
                background: String(year) === String(y) ? GOLD : 'rgba(255,255,255,.10)', color: String(year) === String(y) ? NAVY : '#cdd9ee' }}>
              {y === 'all' ? 'All-time' : y}
            </button>
          ))}
        </div>
      </div>

      {/* tabs */}
      <div style={{ display: 'flex', background: '#fff', borderBottom: '1px solid rgba(30,58,110,.10)', position: 'sticky', top: 0, zIndex: 5 }}>
        {['BATTING', 'BOWLING', 'FIELDING', 'FORM'].map(t => (
          <div key={t} onClick={() => { setTab(t); setZoneSel(null); }} style={{ flex: 1, cursor: 'pointer', textAlign: 'center', padding: '11px 0', fontFamily: 'Rajdhani,sans-serif', fontWeight: 600, letterSpacing: '.05em', fontSize: '0.75rem', color: t === tab ? NAVY : '#c0c8d4', borderBottom: t === tab ? `3px solid ${NAVY}` : '3px solid transparent' }}>{t}</div>
        ))}
      </div>

      {tab === 'BATTING' && (<>
      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 9, padding: '14px 14px 2px' }}>
        <Kpi gold n={num(b.runs)} l="RUNS" s={`${num(b.innings)} inns`} />
        <Kpi n={f1(b.average)} l="AVERAGE" s={`${num(b.not_outs)} NO`} />
        <Kpi n={f1(b.strike_rate)} l="STRIKE RATE" s={`${num(b.balls_faced)} b`} />
        <Kpi n={num(b.highest_score)} l="HIGHEST" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 9, padding: '0 14px 2px' }}>
        {[['FIFTIES', b.fifties], ['HUNDREDS', b.centuries], ['FOURS', b.fours], ['SIXES', b.sixes]].map(([l, v]) => (
          <div key={l} style={{ background: '#fff', border: '1px solid rgba(30,58,110,.10)', borderRadius: 10, padding: '8px 6px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1rem', color: NAVY }}>{num(v)}</div>
            <div style={{ fontSize: '0.55rem', letterSpacing: '.05em', color: '#8190a8', marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Matchups */}
      <Card title="Matchups" hint="FROM BALL-BY-BALL" style={{ marginTop: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 9 }}>
          {nemTop > 0 && <Matchup accent={RED} label={nemTied.length > 1 ? 'NEMESIS — OUT MOST TO (TIED)' : 'NEMESIS — OUT MOST TO'} who={joinNames(nemTied)} meta={`${nemTop} dismissal${nemTop > 1 ? 's' : ''}${nemTied.length > 1 ? ' each' : ''}`} />}
          {bi.favourite && <Matchup accent={GRN} label="SCORES MOST OFF" who={bi.favourite.display_name} meta={`${num(bi.favourite.runs)} runs · SR ${f1(bi.favourite.strike_rate)}`} />}
          {bi.most_fours_off && <Matchup accent={BLUE} label="MOST FOURS OFF" who={`${bi.most_fours_off.display_name} · ${bi.most_fours_off.fours}`} />}
          {bi.most_sixes_off && <Matchup accent={GOLD} label="MOST SIXES OFF" who={`${bi.most_sixes_off.display_name} · ${bi.most_sixes_off.sixes}`} />}
          {bi.kept_quiet_by && <Matchup accent={SLATE} label="KEPT QUIET BY" who={bi.kept_quiet_by.display_name} meta={`SR ${f1(bi.kept_quiet_by.strike_rate)}`} />}
          {di.strongest && <Matchup accent={GRN} label="FAVOURITE ZONE" who={`${di.strongest.zone} · ${di.strongest.runs}r`} />}
          {b.best_over && <Matchup accent={GOLD} label="MOST RUNS IN AN OVER" who={`${num(b.best_over.over_runs)} runs${b.best_over.bowler ? ` · off ${b.best_over.bowler}` : ''}`} meta={overWhen(b.best_over)} />}
        </div>
        {!bi.favourite && !bi.nemesis && <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>No ball-by-ball matchups for this period (2018-onward matches only).</div>}
      </Card>

      <Card title="Run trend" hint="LAST 24 INNINGS"><RunTrend trend={b.runs_trend} /></Card>

      <Card title="Wagon wheel" hint={isLHB ? 'LHB · TAP A ZONE' : 'TAP A ZONE'}>
        <WagonWheel zones={mi.direction_split} selected={zoneSel} onSelect={setZoneSel} lhb={isLHB} />
        {zoneRow ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f3f6fb', border: '1px solid #e3e8f0', borderRadius: 10, padding: '9px 12px', marginTop: 4 }}>
            <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, color: NAVY }}>{zoneRow.zone}</div>
            <div style={{ fontSize: '0.8rem', color: '#4a5a76' }}>{num(zoneRow.runs)} runs · {num(zoneRow.fours)} fours · {num(zoneRow.sixes)} sixes</div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', fontSize: '0.62rem', color: '#9aa7bd', marginTop: 2 }}>{isLHB ? 'OFF RIGHT · LEG LEFT' : 'OFF LEFT · LEG RIGHT'} — tap a zone label for its breakdown</div>
        )}
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 8, fontSize: '0.78rem', color: '#4a5a76' }}>
          <span><span style={{ display: 'inline-block', width: 14, borderTop: `3px solid ${BLUE}`, verticalAlign: 'middle', marginRight: 5 }} />Four</span>
          <span><span style={{ display: 'inline-block', width: 14, borderTop: `3px solid ${RED}`, verticalAlign: 'middle', marginRight: 5 }} />Six</span>
          <span style={{ color: '#9aa7bd' }}>1–3s counted in zone totals</span>
        </div>
        {!(mi.direction_split || []).length && <div style={{ color: '#94a3b8', fontSize: '0.78rem', textAlign: 'center' }}>No shot directions for this period.</div>}
      </Card>

      <Card title="How I get out"><DismissalDonut rows={b.dismissal_breakdown} /></Card>

      <Card title="Score distribution" hint={`${num(b.innings)} innings`}>
        <Bar lab="Ducks" pct={(+b.ducks || 0) / distMax * 100} val={num(b.ducks)} color={RED} />
        <Bar lab="1–9" pct={(+b.scores_1_9 || 0) / distMax * 100} val={num(b.scores_1_9)} />
        <Bar lab="10–19" pct={(+b.scores_10_19 || 0) / distMax * 100} val={num(b.scores_10_19)} />
        <Bar lab="20–29" pct={(+b.scores_20_29 || 0) / distMax * 100} val={num(b.scores_20_29)} />
        <Bar lab="30–49" pct={(+b.scores_30_49 || 0) / distMax * 100} val={num(b.scores_30_49)} />
        <Bar lab="50+" pct={fiftyPlus / distMax * 100} val={num(fiftyPlus)} color={GRN} />
      </Card>

      <Card title="By batting position" hint="AVG · SR">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
          <tbody>
            {(b.by_position || []).map((p, i) => (
              <tr key={i}>
                <td style={{ padding: '7px 0', borderBottom: '1px solid #f0f3f7', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, color: NAVY }}>#{p.pos}</td>
                <td style={{ padding: '7px 0', borderBottom: '1px solid #f0f3f7' }}>{p.innings} inns</td>
                <td style={{ padding: '7px 0', borderBottom: '1px solid #f0f3f7', textAlign: 'right', color: '#5f6f8a' }}>avg {f1(p.avg_runs)} · SR {f1(p.avg_sr)}</td>
              </tr>
            ))}
            {!(b.by_position || []).length && <tr><td style={{ padding: 8, color: '#94a3b8' }}>No position data.</td></tr>}
          </tbody>
        </table>
      </Card>
      </>)}

      {tab === 'BOWLING' && (<>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 9, padding: '14px 14px 2px' }}>
          <Kpi gold n={num(bw.wickets)} l="WICKETS" s={`${f1(bw.overs)} ov`} />
          <Kpi n={f1(bw.economy)} l="ECONOMY" s={`${num(bw.maidens)} mdn`} />
          <Kpi n={f1(bw.average)} l="AVERAGE" s={`${num(bw.runs_conceded)} runs`} />
          <Kpi n={bestFig} l="BEST" s={bw.best_figures ? 'in an inns' : ''} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 9, padding: '0 14px 2px' }}>
          {[['3-FERS', bw.three_wkt_hauls], ['5-FERS', bw.five_wkt_hauls], ['WIDES', bw.wides], ['NO-BALLS', bw.noballs]].map(([l, v]) => (
            <div key={l} style={{ background: '#fff', border: '1px solid rgba(30,58,110,.10)', borderRadius: 10, padding: '8px 6px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1rem', color: NAVY }}>{num(v)}</div>
              <div style={{ fontSize: '0.55rem', letterSpacing: '.05em', color: '#8190a8', marginTop: 2 }}>{l}</div>
            </div>
          ))}
        </div>

        <Card title="Matchups" hint="FROM BALL-BY-BALL" style={{ marginTop: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 9 }}>
            {vbTop > 0 && <Matchup accent={GRN} label={vbTied.length > 1 ? 'BUNNY — DISMISSED MOST (TIED)' : 'BUNNY — DISMISSED MOST'} who={joinNames(vbTied)} meta={`${vbTop} wkt${vbTop > 1 ? 's' : ''}${vbTied.length > 1 ? ' each' : ''}`} />}
            {bwi.punished_by && <Matchup accent={RED} label="PUNISHED MOST BY" who={bwi.punished_by.display_name} meta={`${num(bwi.punished_by.runs_conceded)} runs · econ ${f1(bwi.punished_by.economy)}`} />}
            {bwi.tightest_vs && <Matchup accent={BLUE} label="TIGHTEST AGAINST" who={bwi.tightest_vs.display_name} meta={`econ ${f1(bwi.tightest_vs.economy)}`} />}
            {bw.best_over && <Matchup accent={GRN} label="BEST OVER" who={`${num(bw.best_over.over_runs)} runs`} meta={overWhen(bw.best_over)} />}
            {bw.worst_over && <Matchup accent={RED} label="MOST EXPENSIVE OVER" who={`${num(bw.worst_over.over_runs)} runs${bw.worst_over.batsman ? ` · to ${bw.worst_over.batsman}` : ''}`} meta={overWhen(bw.worst_over)} />}
          </div>
          {!vbTop && !bw.best_over && <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>No ball-by-ball bowling matchups for this period.</div>}
        </Card>

        <Card title="How I take wickets"><DismissalDonut rows={(bw.wicket_types || []).map(w => ({ dismissal: w.dismissal, count: w.count }))} /></Card>

        <Card title="Economy by phase" hint="RUNS PER OVER">
          <Bar lab="Powerplay" pct={(+bw.phase?.powerplay_economy || 0) / phaseEcoMax * 100} val={f1(bw.phase?.powerplay_economy)} color={GRN} />
          <Bar lab="Middle" pct={(+bw.phase?.middle_economy || 0) / phaseEcoMax * 100} val={f1(bw.phase?.middle_economy)} />
          <Bar lab="Death" pct={(+bw.phase?.death_economy || 0) / phaseEcoMax * 100} val={f1(bw.phase?.death_economy)} color={RED} />
          <div style={{ fontSize: '0.7rem', color: '#9aa7bd', marginTop: 6 }}>Dot-ball %: {num(bw.phase?.dot_ball_pct)}% · boundaries conceded: {num(bw.phase?.boundaries_conceded)}</div>
        </Card>

        <Card title="Most wickets against" hint="TOP BATTERS">
          {(mi.vs_batters || []).filter(x => (+x.wickets || 0) > 0).slice(0, 6).map((x, i) => (
            <Bar key={i} lab={x.display_name} pct={(+x.wickets || 0) / bowlWktMax * 100} val={num(x.wickets)} />
          ))}
          {!(mi.vs_batters || []).some(x => (+x.wickets || 0) > 0) && <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>No ball-by-ball wickets for this period.</div>}
        </Card>
      </>)}

      {tab === 'FIELDING' && (<>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 9, padding: '14px 14px 2px' }}>
          <Kpi gold n={num((+fd.catches || 0) + (+fd.stumpings || 0) + (+fd.run_outs || 0))} l="DISMISSALS" />
          <Kpi n={num(fd.catches)} l="CATCHES" />
          <Kpi n={num(fd.stumpings)} l="STUMPINGS" />
          <Kpi n={num(fd.run_outs)} l="RUN-OUTS" />
        </div>
        <Card title="Dismissal mix" style={{ marginTop: 12 }}>
          <DismissalDonut rows={[{ dismissal: 'caught', count: +fd.catches || 0 }, { dismissal: 'stumped', count: +fd.stumpings || 0 }, { dismissal: 'run_out', count: +fd.run_outs || 0 }]} />
        </Card>
        <Card title="Notable">
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#4a5a76', padding: '4px 0' }}>
            <span>Most catches in a match</span><span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, color: NAVY }}>{num(fd.most_catches_match)}</span>
          </div>
          <div style={{ fontSize: '0.7rem', color: '#9aa7bd', marginTop: 4 }}>Fielding is recorded as match totals, so per-shot detail isn't available.</div>
        </Card>
      </>)}

      {tab === 'FORM' && (<>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 9, padding: '14px 14px 2px' }}>
          <Kpi gold n={num(sumRuns(lastN(5)))} l="RUNS · L5" />
          <Kpi n={f1(formAvg)} l="AVG · L10" />
          <Kpi n={f1(formSR)} l="SR · L10" />
          <Kpi n={num(lastN(8).filter(r => (+r.runs || 0) >= 50).length)} l="50+ · L8" />
        </div>

        <Card title="Recent scores" hint="LAST 12 INNINGS" style={{ marginTop: 12 }}><RunTrend trend={lastN(12)} /></Card>

        <Card title="Last results" hint="NEWEST RIGHT">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {results.length ? results.slice(-12).map((r, i) => {
              const win = r.result === 'W' || r.result === 'MOM';
              return (
                <div key={i} style={{ position: 'relative', width: 26, height: 26, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.7rem', color: '#fff', background: win ? GRN : RED }}>
                  {win ? 'W' : 'L'}
                  {r.result === 'MOM' && <span style={{ position: 'absolute', top: -6, right: -5, fontSize: '0.7rem' }}>⭐</span>}
                </div>
              );
            }) : <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>No recent results.</div>}
          </div>
          <div style={{ fontSize: '0.66rem', color: '#9aa7bd', marginTop: 8 }}>⭐ = Man of the Match · {num(pts.motm_count)} total MOM</div>
        </Card>

        <Card title="Recent innings" hint="RUNS (BALLS)">
          {lastN(8).slice().reverse().map((r, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f0f3f7', fontSize: '0.82rem' }}>
              <span style={{ color: '#5f6f8a' }}>{r.match_date ? new Date(r.match_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}</span>
              <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, color: (+r.runs || 0) >= 50 ? GRN : NAVY }}>
                {num(r.runs)}{(!r.dismissal || r.dismissal === 'not_out') ? '*' : ''} <span style={{ color: '#9aa7bd', fontWeight: 400 }}>({num(r.balls)})</span>
              </span>
            </div>
          ))}
          {!trend.length && <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>No innings data.</div>}
        </Card>

        {cap.matches_led > 0 && (
          <Card title="Captaincy">
            <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
              {[['LED', cap.matches_led], ['WON', cap.won], ['LOST', cap.lost]].map(([l, v]) => (
                <div key={l}><div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1.2rem', color: NAVY }}>{num(v)}</div><div style={{ fontSize: '0.58rem', color: '#8190a8', letterSpacing: '.05em' }}>{l}</div></div>
              ))}
            </div>
          </Card>
        )}
      </>)}
    </div>
  );
}
